import { useMemo, useState } from 'react'
import { ApiError } from '../api/client'
import {
  useCostCenterCoverage,
  useCostCenters,
  useCreateCostCenter,
  useDeleteCostCenter,
  useMoveCostCenter,
  usePeriods,
  useUpdateCostCenter,
} from '../api/hooks'
import type { CostCenter, CostCenterCoverage } from '../api/types'
import { NewNodeBadge, NodeCoverageBadge } from '../components/Badges'

/**
 * Screen 1 — Cost Center Tree (§10).
 *
 * Drag to reparent with cycle prevention, inline add and rename, active and allocable
 * toggles. Each node badges how many drivers target it, so a zero-coverage node is
 * visible in the tree itself rather than only on the QC screen.
 */
export function CostCenterTree() {
  const [includeInactive, setIncludeInactive] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [dragId, setDragId] = useState<number | null>(null)
  const [dropTargetId, setDropTargetId] = useState<number | null | 'root'>(null)
  const [error, setError] = useState<string | null>(null)
  const [addingUnder, setAddingUnder] = useState<number | null | 'root'>(null)

  const { data: flat = [], isLoading } = useCostCenters('flat', includeInactive)
  const { data: periods = [] } = usePeriods()
  const periodId = periods[0]?.periodId ?? null
  const { data: coverage = [] } = useCostCenterCoverage(periodId)

  const move = useMoveCostCenter()
  const remove = useDeleteCostCenter()

  const coverageById = useMemo(() => {
    const map = new Map<number, CostCenterCoverage>()
    for (const c of coverage) map.set(c.costCenterId, c)
    return map
  }, [coverage])

  const byId = useMemo(() => {
    const map = new Map<number, CostCenter>()
    for (const n of flat) map.set(n.costCenterId, n)
    return map
  }, [flat])

  /**
   * Whether `candidateParent` is inside `node`'s own subtree.
   *
   * Prefix-matching the materialized path is the same test the stored procedure makes,
   * so the UI refuses the drop for exactly the reason the server would — the user finds
   * out while dragging rather than after releasing.
   */
  function wouldCreateCycle(nodeId: number, candidateParentId: number): boolean {
    const node = byId.get(nodeId)
    const candidate = byId.get(candidateParentId)
    if (!node || !candidate) return false
    return candidate.materializedPath.startsWith(node.materializedPath)
  }

  function handleDrop(targetId: number | 'root') {
    setDropTargetId(null)
    const sourceId = dragId
    setDragId(null)
    if (sourceId === null) return

    const newParentId = targetId === 'root' ? null : targetId
    if (newParentId !== null && wouldCreateCycle(sourceId, newParentId)) {
      setError('A cost center cannot become a descendant of itself.')
      return
    }
    if (byId.get(sourceId)?.parentCostCenterId === newParentId) return

    setError(null)
    move.mutate(
      { id: sourceId, newParentId },
      { onError: (e) => setError(e instanceof ApiError ? e.message : String(e)) },
    )
  }

  function handleDelete(node: CostCenter) {
    setError(null)
    remove.mutate(node.costCenterId, {
      onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
    })
  }

  return (
    <div className="flex h-full">
      <section className="flex min-w-0 flex-1 flex-col">
        <Toolbar
          includeInactive={includeInactive}
          onIncludeInactiveChange={setIncludeInactive}
          onAddRoot={() => setAddingUnder('root')}
          periodCode={periods[0]?.periodCode ?? null}
        />

        {error && (
          <div
            className="border-b px-4 py-2 text-[12px]"
            style={{
              background: 'var(--color-error-wash)',
              color: 'var(--color-error)',
              borderColor: 'var(--color-rule-soft)',
            }}
            role="alert"
          >
            {error}
            <button className="ml-2 underline" onClick={() => setError(null)}>
              dismiss
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
          {isLoading ? (
            <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
              Loading…
            </p>
          ) : (
            <table className="w-full border-collapse">
              <thead
                className="sticky top-0 z-10"
                style={{ background: 'var(--color-panel-alt)' }}
              >
                <tr
                  className="border-b text-left text-[11px] uppercase tracking-wide"
                  style={{ borderColor: 'var(--color-rule)', color: 'var(--color-muted)' }}
                >
                  <th className="px-3 py-1.5 font-medium">Cost center</th>
                  <th className="px-3 py-1.5 font-medium">Code</th>
                  <th className="px-3 py-1.5 font-medium">Coverage</th>
                  <th className="px-3 py-1.5 text-right font-medium">Children</th>
                  <th className="px-3 py-1.5 font-medium">Flags</th>
                  <th className="px-3 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {/* Dropping here promotes a node to a root. */}
                <tr
                  onDragOver={(e) => {
                    e.preventDefault()
                    setDropTargetId('root')
                  }}
                  onDragLeave={() => setDropTargetId(null)}
                  onDrop={() => handleDrop('root')}
                  style={{
                    background: dropTargetId === 'root' ? 'var(--color-accent-wash)' : undefined,
                  }}
                >
                  <td
                    colSpan={6}
                    className="px-3 py-1 text-[11px] italic"
                    style={{ color: 'var(--color-faint)' }}
                  >
                    {dragId !== null ? 'drop here to make this a root' : ' '}
                  </td>
                </tr>

                {flat.map((node) => {
                  const cov = coverageById.get(node.costCenterId)
                  const isDropTarget = dropTargetId === node.costCenterId
                  const invalidTarget =
                    dragId !== null &&
                    dragId !== node.costCenterId &&
                    wouldCreateCycle(dragId, node.costCenterId)

                  return (
                    <tr
                      key={node.costCenterId}
                      className="grid-row border-b"
                      style={{
                        borderColor: 'var(--color-rule-soft)',
                        background: isDropTarget
                          ? invalidTarget
                            ? 'var(--color-error-wash)'
                            : 'var(--color-accent-wash)'
                          : undefined,
                        opacity: node.isActive ? 1 : 0.55,
                      }}
                      data-selected={selectedId === node.costCenterId}
                      tabIndex={0}
                      onClick={() => setSelectedId(node.costCenterId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedId(node.costCenterId)
                        }
                      }}
                      draggable
                      onDragStart={() => setDragId(node.costCenterId)}
                      onDragEnd={() => {
                        setDragId(null)
                        setDropTargetId(null)
                      }}
                      onDragOver={(e) => {
                        e.preventDefault()
                        setDropTargetId(node.costCenterId)
                      }}
                      onDragLeave={() => setDropTargetId(null)}
                      onDrop={() => handleDrop(node.costCenterId)}
                    >
                      <td className="px-3 py-1">
                        <span
                          style={{ paddingLeft: `${node.depth * 18}px` }}
                          className="inline-flex items-center gap-2"
                        >
                          <span
                            style={{ color: 'var(--color-faint)' }}
                            aria-hidden
                            className="select-none"
                          >
                            {node.hasChildren ? '▾' : '·'}
                          </span>
                          <span className="font-medium">{node.name}</span>
                          {cov?.createdSinceLastRun && <NewNodeBadge />}
                        </span>
                      </td>

                      <td
                        className="px-3 py-1 font-mono text-[12px]"
                        style={{ color: 'var(--color-muted)' }}
                      >
                        {node.code}
                      </td>

                      <td className="px-3 py-1">
                        {cov ? (
                          <NodeCoverageBadge
                            driverCount={cov.targetingDriverCount}
                            isAllocable={cov.isAllocable}
                          />
                        ) : (
                          <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
                            —
                          </span>
                        )}
                      </td>

                      <td className="num px-3 py-1" style={{ color: 'var(--color-muted)' }}>
                        {node.descendantCount}
                      </td>

                      <td className="px-3 py-1">
                        <span className="flex gap-1 text-[11px]">
                          {!node.isAllocable && (
                            <span
                              className="rounded px-1.5 py-0.5"
                              style={{
                                background: 'var(--color-panel-alt)',
                                color: 'var(--color-muted)',
                              }}
                              title="Deliberately never posted to. Unrelated to whether it has children."
                            >
                              not allocable
                            </span>
                          )}
                          {!node.isActive && (
                            <span
                              className="rounded px-1.5 py-0.5"
                              style={{
                                background: 'var(--color-panel-alt)',
                                color: 'var(--color-muted)',
                              }}
                            >
                              inactive
                            </span>
                          )}
                        </span>
                      </td>

                      <td className="px-3 py-1 text-right whitespace-nowrap">
                        <button
                          className="mr-2 text-[11px] underline"
                          style={{ color: 'var(--color-accent)' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            setAddingUnder(node.costCenterId)
                          }}
                          title="Adding a child under a cost center that already receives allocations is normal and unblocked. The parent keeps its own allocation."
                        >
                          add child
                        </button>
                        <button
                          className="text-[11px] underline"
                          style={{ color: 'var(--color-muted)' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(node)
                          }}
                        >
                          delete
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <aside
        className="w-80 shrink-0 overflow-auto border-l"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        {addingUnder !== null ? (
          <AddNodeForm
            parentId={addingUnder === 'root' ? null : addingUnder}
            parentName={
              addingUnder === 'root' ? null : (byId.get(addingUnder as number)?.name ?? null)
            }
            onDone={() => setAddingUnder(null)}
          />
        ) : selectedId !== null && byId.has(selectedId) ? (
          <EditNodeForm key={selectedId} node={byId.get(selectedId)!} />
        ) : (
          <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            Select a cost center to edit it, or drag a row onto another to reparent it.
          </p>
        )}
      </aside>
    </div>
  )
}

function Toolbar({
  includeInactive,
  onIncludeInactiveChange,
  onAddRoot,
  periodCode,
}: {
  includeInactive: boolean
  onIncludeInactiveChange: (v: boolean) => void
  onAddRoot: () => void
  periodCode: string | null
}) {
  return (
    <div
      className="flex items-center gap-4 border-b px-4 py-2"
      style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
    >
      <h1 className="text-[14px] font-semibold">Cost Center Tree</h1>

      <button
        className="rounded border px-2 py-1 text-[12px]"
        style={{ borderColor: 'var(--color-rule)' }}
        onClick={onAddRoot}
      >
        Add root
      </button>

      <label className="flex items-center gap-1.5 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => onIncludeInactiveChange(e.target.checked)}
        />
        Show inactive
      </label>

      <span className="ml-auto text-[11px]" style={{ color: 'var(--color-faint)' }}>
        {periodCode ? `Coverage for ${periodCode}` : 'No period'}
      </span>
    </div>
  )
}

function AddNodeForm({
  parentId,
  parentName,
  onDone,
}: {
  parentId: number | null
  parentName: string | null
  onDone: () => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [isAllocable, setIsAllocable] = useState(true)
  const [violations, setViolations] = useState<Record<string, string>>({})
  const create = useCreateCostCenter()

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setViolations({})
    create.mutate(
      { code: code.trim(), name: name.trim(), parentCostCenterId: parentId, isAllocable },
      {
        onSuccess: onDone,
        onError: (err) =>
          setViolations(
            err instanceof ApiError
              ? Object.keys(err.violations).length > 0
                ? err.violations
                : { _: err.message }
              : { _: String(err) },
          ),
      },
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 p-4">
      <h2 className="text-[13px] font-semibold">
        {parentName ? `Add child under ${parentName}` : 'Add root cost center'}
      </h2>

      {parentName && (
        <p className="text-[11px]" style={{ color: 'var(--color-muted)' }}>
          The parent keeps its own direct allocation. The new child starts with none, and
          QC-C005 will flag it until a driver targets it.
        </p>
      )}

      <Field label="Code" error={violations.code}>
        <input
          className="w-full rounded border px-2 py-1 font-mono text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
        />
      </Field>

      <Field label="Name" error={violations.name}>
        <input
          className="w-full rounded border px-2 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <label className="flex items-center gap-1.5 text-[12px]">
        <input
          type="checkbox"
          checked={isAllocable}
          onChange={(e) => setIsAllocable(e.target.checked)}
        />
        Allocable
      </label>

      {violations._ && (
        <p className="text-[12px]" style={{ color: 'var(--color-error)' }}>
          {violations._}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded px-3 py-1 text-[12px] font-medium text-white"
          style={{ background: 'var(--color-accent)' }}
        >
          {create.isPending ? 'Adding…' : 'Add'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded border px-3 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function EditNodeForm({ node }: { node: CostCenter }) {
  const [code, setCode] = useState(node.code)
  const [name, setName] = useState(node.name)
  const [isAllocable, setIsAllocable] = useState(node.isAllocable)
  const [isActive, setIsActive] = useState(node.isActive)
  const [violations, setViolations] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)
  const update = useUpdateCostCenter()

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setViolations({})
    setSaved(false)
    update.mutate(
      { id: node.costCenterId, code: code.trim(), name: name.trim(), isAllocable, isActive },
      {
        onSuccess: () => setSaved(true),
        onError: (err) =>
          setViolations(
            err instanceof ApiError
              ? Object.keys(err.violations).length > 0
                ? err.violations
                : { _: err.message }
              : { _: String(err) },
          ),
      },
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 p-4">
      <h2 className="text-[13px] font-semibold">{node.pathLabel}</h2>

      <Field label="Code" error={violations.code}>
        <input
          className="w-full rounded border px-2 py-1 font-mono text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </Field>

      <Field label="Name" error={violations.name}>
        <input
          className="w-full rounded border px-2 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <label className="flex items-center gap-1.5 text-[12px]">
        <input
          type="checkbox"
          checked={isAllocable}
          onChange={(e) => setIsAllocable(e.target.checked)}
        />
        Allocable
        <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
          (parents are valid targets)
        </span>
      </label>

      <label className="flex items-center gap-1.5 text-[12px]">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>

      <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]" style={{ color: 'var(--color-muted)' }}>
        <dt>Depth</dt>
        <dd className="num">{node.depth}</dd>
        <dt>Descendants</dt>
        <dd className="num">{node.descendantCount}</dd>
        <dt>Path</dt>
        <dd className="font-mono">{node.materializedPath}</dd>
      </dl>

      {violations._ && (
        <p className="text-[12px]" style={{ color: 'var(--color-error)' }}>
          {violations._}
        </p>
      )}
      {saved && (
        <p className="text-[12px]" style={{ color: 'var(--color-ok)' }}>
          Saved.
        </p>
      )}

      <button
        type="submit"
        disabled={update.isPending}
        className="self-start rounded px-3 py-1 text-[12px] font-medium text-white"
        style={{ background: 'var(--color-accent)' }}
      >
        {update.isPending ? 'Saving…' : 'Save'}
      </button>
    </form>
  )
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>
        {label}
      </span>
      {children}
      {error && (
        <span className="text-[11px]" style={{ color: 'var(--color-error)' }}>
          {error}
        </span>
      )}
    </label>
  )
}
